import {
  Hexagram,
  hexagramChanged,
  hexagramLines,
  hexagramMutual,
  toHexagramDTO,
  trigramElement,
  yuelingWithElement
} from '../core/meihua';
import { hexagramName } from '../core/gua64';
import { assignTiYong, buildReading, elementTag, movingYaoMark } from '../core/tiyong';

/* =====================================================================
 * 问事结构化 + 盘面接地载荷
 * 目的：让 AI 拿到"程序已经算好"的盘面事实（体用、五行、互变生克、旺衰、
 * 卦名、先天数），只负责按梅花易数的次第推理，不再自行推导五行或生克。
 * 服务端 functions/src/meihuaPrompt.ts 会把这些字段排成【卦象数据】。
 * ===================================================================== */

export type MeihuaCategory =
  | 'career' | 'wealth' | 'love' | 'health' | 'study'
  | 'travel' | 'legal' | 'lost' | 'cooperation' | 'general';

export type MeihuaHorizon = 'near' | 'mid' | 'far';

export interface MeihuaCategoryOption {
  key: MeihuaCategory;
  label: string;
  /** 给用户看的取象提示 */
  hint: string;
}

export const MEIHUA_CATEGORIES: MeihuaCategoryOption[] = [
  { key: 'career', label: '事业工作', hint: '看体卦旺衰与用卦是否生体，断能否落实' },
  { key: 'wealth', label: '财运求财', hint: '体克用为得财费力，用生体为财来，用克体主破耗' },
  { key: 'love', label: '感情婚姻', hint: '体用分主客，比和相生主和睦，相克主摩擦' },
  { key: 'health', label: '健康', hint: '看克体之卦的五行对应脏腑，只谈调养与就医提醒' },
  { key: 'study', label: '学业考试', hint: '看体卦状态与文书之象（离、巽）' },
  { key: 'travel', label: '出行搬迁', hint: '取象看震（动）、坎（险）、艮（止）' },
  { key: 'legal', label: '官司竞争', hint: '比体用强弱定优劣，只谈倾向与应对' },
  { key: 'lost', label: '寻物寻人', hint: '以用卦类象与方位定场域，变卦定能否寻回' },
  { key: 'cooperation', label: '合作谈判', hint: '比和为贵，相生主互让，相克主分歧' },
  { key: 'general', label: '综合趋势', hint: '以体用与旺衰定大势，互变定过程与结果' }
];

export const MEIHUA_HORIZONS: { key: MeihuaHorizon; label: string }[] = [
  { key: 'near', label: '近期' },
  { key: 'mid', label: '数月内' },
  { key: 'far', label: '一年以上' }
];

export interface MeihuaGuaPayload {
  original: ReturnType<typeof toHexagramDTO>;
  mutual: ReturnType<typeof toHexagramDTO>;
  changed: ReturnType<typeof toHexagramDTO>;
  bodyTrigram: string;
  useTrigram: string;
  relation: string;
  yueling: string;
  startedAtISO: string;
  /** 程序算定的盘面事实（服务端直接排进提示词） */
  analysis: Record<string, unknown> | null;
}

export interface MeihuaRequestPayload {
  userQuestion: string;
  gua: MeihuaGuaPayload;
  category: MeihuaCategory;
  horizon: MeihuaHorizon;
  background: string;
}

export function buildMeihuaGua(args: {
  original: Hexagram;
  mutual: Hexagram;
  changed: Hexagram;
  date: Date;
}): MeihuaGuaPayload {
  const { original, mutual, changed, date } = args;
  const yueling = yuelingWithElement(date);
  const ti = assignTiYong(original);
  const reading = buildReading(original, mutual, changed, yueling);
  const lines = hexagramLines(original);

  const analysis = reading && ti
    ? {
        names: {
          original: hexagramName(original),
          mutual: hexagramName(mutual),
          changed: hexagramName(changed)
        },
        body: {
          name: elementTagLabel(ti.body),
          element: trigramElement(ti.body),
          pos: ti.bodyPos,
          moving: false
        },
        use: {
          name: elementTagLabel(ti.use),
          element: trigramElement(ti.use),
          pos: ti.usePos,
          moving: true
        },
        pair: { key: reading.pair.key, grade: reading.pair.tone, kind: reading.pair.kind },
        movingLine: {
          index: ti.movingLine,
          yao: ti.yaoName,
          pos: ti.movingIn,
          mark: movingYaoMark(lines[ti.movingLine - 1])
        },
        mutual: reading.mutualRefs.map((r) => ({
          role: r.pos === 'upper' ? '互上' : '互下',
          name: elementTagLabel(r.trigram),
          element: trigramElement(r.trigram),
          rel: r.ref.key,
          grade: r.ref.score > 0 ? '吉' : r.ref.score < 0 ? '凶' : '平'
        })),
        changed: {
          name: elementTagLabel(reading.changedRef.trigram),
          element: trigramElement(reading.changedRef.trigram),
          rel: reading.changedRef.ref.key,
          grade: reading.changedRef.ref.score > 0 ? '吉' : reading.changedRef.ref.score < 0 ? '凶' : '平'
        },
        yueling: { text: yueling, effect: reading.ling ? reading.ling.label : '月令未定' },
        scores: { total: Math.round(reading.total * 10) / 10, tone: reading.tone },
        xiantian: { body: ti.body as number, use: ti.use as number }
      }
    : null;

  return {
    original: toHexagramDTO(original),
    mutual: toHexagramDTO(mutual),
    changed: toHexagramDTO(changed),
    bodyTrigram: ti ? elementTagLabel(ti.body) : '',
    useTrigram: ti ? elementTagLabel(ti.use) : '',
    relation: reading ? reading.pair.key : '',
    yueling,
    startedAtISO: date.toISOString(),
    analysis
  };
}

/** 卦名（单字），用于 analysis 里的 name 字段 */
function elementTagLabel(t: Parameters<typeof trigramElement>[0]): string {
  return elementTag(t).slice(0, 1);
}

export function buildMeihuaRequest(args: {
  question: string;
  original: Hexagram;
  mutual: Hexagram;
  changed: Hexagram;
  date: Date;
  category: MeihuaCategory;
  horizon: MeihuaHorizon;
  background: string;
}): MeihuaRequestPayload {
  return {
    userQuestion: args.question.trim(),
    gua: buildMeihuaGua({
      original: args.original,
      mutual: args.mutual,
      changed: args.changed,
      date: args.date
    }),
    category: args.category,
    horizon: args.horizon,
    background: args.background.trim()
  };
}

/** 问事写法提示（UI 用）：一事一占，问得具体可断 */
export const MEIHUA_QUESTION_TIPS = [
  '一事一占：一卦只问一件事，不要把工作、感情、健康混在一问里。',
  '问得具体：写清对象与时间范围，如"三个月内能否拿到 A 公司的 offer"。',
  '问可控之事：以自己能否推进为主语，比"他会不会喜欢我"更容易落地。',
  '卦由起卦时刻生成，所以提问要一次说清：先写好问题再点解读。'
];

export { hexagramChanged, hexagramMutual };
